import React, { useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { DOCUMENT } from '../constants/documentTheme';
import { getDestinationPhotoUrl } from '../utils/partnerListUtils';
import {
  getPipelineCardId,
  getPipelineColumnId,
  pipelineBoardId,
  resolvePipelineDrop,
} from '../utils/pipelineBoardDragUtils';

const pipelineCollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length) return pointerHits;
  return closestCorners(args);
};

const MOUSE_SENSOR_OPTIONS = {
  activationConstraint: { distance: 8 },
};

const TOUCH_SENSOR_OPTIONS = {
  activationConstraint: { delay: 200, tolerance: 8 },
};

const formatPipelineDates = (trip) => {
  if (!trip.start_date && !trip.end_date) return '';
  if (trip.start_date && trip.end_date) {
    return `${format(new Date(trip.start_date), 'MMM d')} — ${format(new Date(trip.end_date), 'MMM d, yyyy')}`;
  }
  return format(new Date(trip.start_date || trip.end_date), 'MMM d, yyyy');
};

const stageSelectSx = {
  borderRadius: 0,
  fontFamily: DOCUMENT.sans,
  fontSize: 12,
  color: DOCUMENT.ink,
  bgcolor: DOCUMENT.canvas,
  '& .MuiSelect-select': { py: 0.75, px: 1.25 },
  '& .MuiSvgIcon-root': { color: DOCUMENT.ink2 },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: 0,
    borderColor: DOCUMENT.rule,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: DOCUMENT.oxblood },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: DOCUMENT.oxblood,
    borderWidth: 1,
  },
};

const stageMenuProps = {
  PaperProps: {
    elevation: 0,
    sx: {
      borderRadius: 0,
      mt: 0.5,
      maxHeight: 240,
      bgcolor: DOCUMENT.surface,
      border: `1px solid ${DOCUMENT.rule}`,
      boxShadow: 'none',
      '& .MuiMenuItem-root': {
        fontFamily: DOCUMENT.sans,
        fontSize: 13,
        color: DOCUMENT.ink,
        '&:hover': { bgcolor: DOCUMENT.ivory },
        '&.Mui-selected': { bgcolor: DOCUMENT.oxblood, color: DOCUMENT.ivory },
        '&.Mui-selected:hover': { bgcolor: DOCUMENT.oxblood },
      },
    },
  },
};

const PipelineCardBody = ({ trip }) => {
  const image = trip.image_url || getDestinationPhotoUrl(trip.first_location || trip.title, trip.id);
  const dates = formatPipelineDates(trip);

  return (
    <>
      <Box sx={{ height: 96, position: 'relative', bgcolor: DOCUMENT.rule, overflow: 'hidden' }}>
        {image ? (
          <Box
            component="img"
            src={image}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : null}
      </Box>
      <Box sx={{ p: 1.25, pb: 1 }}>
        <Typography sx={{
          fontFamily: DOCUMENT.display,
          fontWeight: 400,
          fontSize: 16,
          color: DOCUMENT.ink,
          lineHeight: 1.2,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        >
          {trip.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: DOCUMENT.ink2, mt: 0.5 }}>
          {trip.client_name || '—'}
        </Typography>
        {dates ? (
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
            {dates}
          </Typography>
        ) : null}
      </Box>
    </>
  );
};

const SortablePipelineCard = ({
  trip,
  stage,
  stages,
  updating,
  onOpen,
  onStageChange,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getPipelineCardId(trip),
    data: {
      type: 'card',
      stage,
      boardId: pipelineBoardId(trip),
      cardType: trip.cardType || 'trip',
      tripId: trip.tripId || (trip.cardType === 'inquiry' ? null : trip.id),
      inquiryId: trip.inquiryId || (trip.cardType === 'inquiry' ? trip.id : null),
    },
    disabled: updating,
  });

  return (
    <Box
      ref={setNodeRef}
      data-testid="partner-pipeline-card"
      {...attributes}
      {...listeners}
      onClick={() => onOpen(trip)}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{
        flexShrink: 0,
        border: `1px solid ${isDragging ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
        bgcolor: DOCUMENT.surface,
        borderRadius: 0,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.35 : 1,
        boxShadow: 'none',
        '&:hover': { borderColor: DOCUMENT.oxblood },
      }}
    >
      <PipelineCardBody trip={trip} />
      <Box
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{ px: 1.25, pb: 1.25 }}
      >
        <FormControl fullWidth size="small">
          <Select
            value={trip.pipeline_stage || stage}
            onChange={(event) => onStageChange(pipelineBoardId(trip), event.target.value)}
            disabled={updating}
            inputProps={{ 'aria-label': t('partnerPipeline.moveTo') }}
            MenuProps={stageMenuProps}
            sx={stageSelectSx}
          >
            {stages.map((stageKey) => (
              <MenuItem key={stageKey} value={stageKey}>
                {t(`partnerPipeline.stages.${stageKey}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

const PipelineColumn = ({
  stage,
  trips,
  stages,
  stageUpdatingId,
  onOpenTrip,
  onStageChange,
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: getPipelineColumnId(stage),
    data: { type: 'column', stage },
  });
  const itemIds = trips.map((trip) => getPipelineCardId(trip));

  return (
    <Box
      ref={setNodeRef}
      data-testid="partner-pipeline-column"
      data-stage={stage}
      sx={{
        flex: '1 1 0',
        minWidth: 220,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: isOver ? '#F3EDE3' : DOCUMENT.canvas,
        border: `1px solid ${isOver ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.15,
          flexShrink: 0,
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: DOCUMENT.surface,
        }}
      >
        <Typography sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink2,
        }}
        >
          {t(`partnerPipeline.stages.${stage}`)}
        </Typography>
        <Chip
          label={trips.length}
          size="small"
          sx={{
            borderRadius: 0,
            height: 20,
            minWidth: 22,
            bgcolor: DOCUMENT.canvas,
            border: `1px solid ${DOCUMENT.rule}`,
            '& .MuiChip-label': { px: 0.75, fontSize: 11, fontVariantNumeric: 'tabular-nums' },
          }}
        />
      </Box>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {trips.map((trip) => (
            <SortablePipelineCard
              key={pipelineBoardId(trip)}
              trip={trip}
              stage={stage}
              stages={stages}
              updating={stageUpdatingId === pipelineBoardId(trip)}
              onOpen={onOpenTrip}
              onStageChange={onStageChange}
            />
          ))}
        </Box>
      </SortableContext>
    </Box>
  );
};

const PartnerTripPipelineBoard = ({
  stages,
  tripsByStage,
  stageUpdatingId,
  onOpenTrip,
  onStageChange,
}) => {
  const skipClickRef = useRef(false);
  const [activeTrip, setActiveTrip] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const tripsById = useMemo(() => {
    const map = new Map();
    Object.values(tripsByStage || {}).forEach((trips) => {
      trips.forEach((trip) => map.set(pipelineBoardId(trip), trip));
    });
    return map;
  }, [tripsByStage]);

  const handleOpenTrip = (trip) => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    onOpenTrip(trip);
  };

  const handleDragStart = ({ active }) => {
    skipClickRef.current = false;
    const boardId = active?.data?.current?.boardId;
    setActiveTrip(tripsById.get(boardId) || null);
  };

  const handleDragEnd = (event) => {
    if (event.delta && (Math.abs(event.delta.x) > 4 || Math.abs(event.delta.y) > 4)) {
      skipClickRef.current = true;
    }
    const move = resolvePipelineDrop(event);
    setActiveTrip(null);
    if (!move) return;
    onStageChange(move.boardId, move.toStage);
  };

  const handleDragCancel = () => {
    skipClickRef.current = true;
    setActiveTrip(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pipelineCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box
        data-testid="partner-trips-board"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'stretch',
          gap: 1.25,
          overflowX: 'auto',
          overflowY: 'hidden',
          mt: 1.25,
          pb: 0.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {stages.map((stage) => (
          <PipelineColumn
            key={stage}
            stage={stage}
            stages={stages}
            trips={tripsByStage[stage] || []}
            stageUpdatingId={stageUpdatingId}
            onOpenTrip={handleOpenTrip}
            onStageChange={onStageChange}
          />
        ))}
      </Box>
      <DragOverlay dropAnimation={null} style={{ zIndex: 1600 }}>
        {activeTrip ? (
          <Box
            sx={{
              width: 240,
              border: `1px solid ${DOCUMENT.oxblood}`,
              bgcolor: DOCUMENT.surface,
              borderRadius: 0,
              overflow: 'hidden',
              boxShadow: 'none',
              cursor: 'grabbing',
            }}
          >
            <PipelineCardBody trip={activeTrip} />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default PartnerTripPipelineBoard;
